import referencePool from "./references.js";
import React from "react";
export const CITATION_DEDUPE_AUTHORS = true; // set to true if you ever want 1-per-author

/** Fold/normalize text (Žižek->zizek, spaces, etc.) */
function fold(s = "") {
  return s
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/** Extract a compact author key from an APA inline string like "Bhaskar, 1993" */
function authorKeyFromInline(cite = "") {
  // take text before comma/year, split "&" or "and", keep first chunk, fold
  const head = cite.split(/\s*,\s*\d{4}\b/)[0] || cite;
  const first = head.split(/\s*(?:&| and )\s*/i)[0] || head;
  return fold(first.trim());
}

function sortBackendRefs(refs = []) {
  return refs
    .map((r) => toAPA7Inline(typeof r === "string" ? r : r.citation || ""))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

/** Capitalizes the first letter of a string. */
function capitalizeFirst(s) {
  if (!s || typeof s !== "string") return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Detects if a sentence already ends with an APA-like citation, e.g., (Author, 2002) */
function hasExistingCitation(sentence) {
  return /\([A-Z][A-Za-zÀ-ÿ&.\-'\s]+,\s*\d{4}\)\.?$/.test(sentence.trim());
}

/** Basic guard to avoid injecting into headings or list items */
function looksLikeHeadingOrList(sentence) {
  return /^(\s*[-*•]\s+|#{1,6}\s+|Step\s*\d+[:\-]\s*)/i.test(sentence);
}

// ----- STEP DETERMINISM (DAT.docx-aligned) -----

// hard constraint: exactly Step 1 only
// allow specific refs to appear in Step 1 summaries (strict whitelist)
const STEP1_WHITELIST = new Set(["Aristotle1984", "Tarski1944"]);

const RANDOMIZE_TIED_SCORES = false; // set true if you want mild rotation on ties

// Hard caps per step (absolute ceilings)
const STEP_CAPS = {
  "Step 1": 1,
  "Step 2": 1,
  "Step 3": 2,
  "Step 4": 2,
};

const ONLY_STEP1 = new Set([
  "Aristotle1984", // contradiction gatekeeping only
]);

// minimum step required for appearance (others are liberal)
const MIN_STEP = {
  Zizek1989: 3, // allowed Step 3–4
  Zizek2006: 4, // Step 4 only
  Zizek2008: 4, // Step 4 only
  Lacan2007: 4, // Step 4 only
};

function stepNumber(step = "") {
  const s = (step || "").toLowerCase();
  if (s.includes("step 1")) return 1;
  if (s.includes("step 2")) return 2;
  if (s.includes("step 3")) return 3;
  if (s.includes("step 4")) return 4;
  return 0; // unknown/unset -> treat as liberal
}

function isRefAllowedAtStep(refKey = "", step = "") {
  if (!refKey) return true;
  const n = stepNumber(step);

  // strictly Step 1 only
  if (ONLY_STEP1.has(refKey)) return n === 1 || n === 0; // if step unknown, allow (liberal)

  // min step gates (e.g., Lacan/Žižek)
  const min = MIN_STEP[refKey];
  if (min && n > 0 && n < min) return false;

  // otherwise liberal
  return true;
}

/** Hard block: never inject citations for Step 1 / Contradiction Identification */
export function isStepOne(stepLabel = "") {
  const s = (stepLabel || "").toLowerCase();
  return s.includes("step 1") || s.includes("contradiction identification");
}

/** Prefer backend recommendations; otherwise fall back to pool-based relevance (Step 1 blocked elsewhere) */
export function getCitationsForStep({
  text = "",
  backendRefs = [],
  step = "",
  max = 0,
  excludeAuthors = [],
  preferTags = [],
}) {
  if (!text) return [];

  // authors to skip globally (folded for robust compare)
  const excludedSet = new Set(excludeAuthors.map(fold));

  // ── STEP 1: whitelist only (e.g., Aristotle)
  if (isStepOne(step)) {
    const selected = [];
    const seen = new Set(); // per-author dedupe by first author

    for (const inline of sortBackendRefs(backendRefs)) {
      const akey = authorKeyFromInline(inline);
      if (!akey) continue;

      const ymatch = inline.match(/\b(\d{4})\b/);
      const year = ymatch ? ymatch[1] : null;

      // map inline → pool entry so we can check whitelist + year
      const match = referencePool.find((ref) => {
        if (!STEP1_WHITELIST.has(ref.key)) return false;
        const refAuthorKey = authorKeyFromInline(`${ref.authors}, ${ref.year}`);
        return (
          (!akey || akey === refAuthorKey) &&
          (!year || String(ref.year) === year)
        );
      });
      if (!match) continue;

      const cleaned = toAPA7Inline(match.citation);
      if (!cleaned) continue;

      const akey2 = authorKeyFromInline(cleaned);
      if (!akey2 || excludedSet.has(akey2) || seen.has(akey2)) continue;

      seen.add(akey2);
      selected.push(cleaned);
      if (selected.length >= max) break;
    }

    if (selected.length > 0) return selected;

    // fallback: heuristic, but still whitelist-gated
    return getRelevantCitations(text, max, {
      step,
      whitelistKeys: [...STEP1_WHITELIST],
    });
  }

  // ── NON-STEP 1: deterministic backend refs first, de-dupe by author
  {
    const selected = [];
    const seen = new Set();

    for (const inline of sortBackendRefs(backendRefs)) {
      const akey = authorKeyFromInline(inline);
      if (!akey) continue;
      if (excludedSet.has(akey) || seen.has(akey)) continue;

      seen.add(akey);
      selected.push(inline);
      if (selected.length >= max) break;
    }

    if (selected.length > 0) return selected;
  }

  // ── Fallback: local heuristic
  return getRelevantCitations(text, max, { step, excludeAuthors, preferTags });
}

/** Score a reference against the given text using phrases, claim_keys, and author name. */
function scoreRef(ref, folded) {
  let score = 0;

  // 1) Strong: phrases (exact fragments the author is known for)
  if (Array.isArray(ref.phrases)) {
    for (const p of ref.phrases) {
      const pf = fold(p);
      if (pf && folded.includes(pf)) score += 4;
    }
  }

  // 2) Medium: claim_keys (concepts strongly associated with the work)
  if (Array.isArray(ref.claim_keys)) {
    for (const k of ref.claim_keys) {
      const kf = fold(k);
      if (kf && folded.includes(kf)) score += 2;
    }
  }

  // 3) Fallback: author name match (exact or folded)
  if (ref.authors) {
    const authorFold = fold(ref.authors);
    const authorHead = fold(ref.authors.split(",")[0] || ref.authors);
    if (
      authorFold &&
      (folded.includes(authorFold) || folded.includes(authorHead))
    ) {
      score += 3;
    }
  }

  // 4) Light tag hint
  if (Array.isArray(ref.tags)) {
    for (const t of ref.tags) {
      const tf = fold(t);
      if (tf && folded.includes(tf)) {
        score += 1;
        break;
      }
    }
  }

  return score;
}

/**
 * Returns the top N APA7 inline citations that are semantically relevant to `text`.
 * Uses phrases > claim_keys > author-name matching. Filters out weak matches and duplicates.
 */
/**
 * Returns the top N APA7 inline citations relevant to `text`.
 * - Filters weak matches (score >= 2)
 * - Enforces max 1 citation per author
 * - Supports excluding authors used elsewhere (via options.excludeAuthors)
 * - Optional stage bias via options.preferTags (array of tag strings)
 */
/**
 * Returns the top N APA7 inline citations relevant to `text`.
 * - Filters weak matches (score >= 2)
 * - Enforces max 1 citation per author
 * - Respects step determinism (DAT.docx-aligned) via options.step
 * - Respects excludeAuthors (array of strings)
 * - Lightly biases toward preferTags (array of tag strings)
 */
export function getRelevantCitations(text, max = 0, options = {}) {
  if (!text) return [];
  const {
    step = "",
    excludeAuthors = [],
    preferTags = [],
    whitelistKeys = [],
  } = options;

  const folded = fold(text);
  const MAX = Math.max(0, max | 0);
  if (MAX === 0) return [];

  const MIN_SCORE = 2;
  const excludedKeys = new Set(excludeAuthors.map((a) => fold(a)));
  const whitelistSet = new Set(
    Array.isArray(whitelistKeys) ? whitelistKeys : [],
  );

  const preferTagFolds = Array.isArray(preferTags) ? preferTags.map(fold) : [];

  const ranked = referencePool
    .map((ref) => {
      let base = scoreRef(ref, folded);

      // +1 bias if any preferred tag is on this ref
      if (preferTagFolds.length && Array.isArray(ref.tags)) {
        const hasPref = ref.tags.some((t) => preferTagFolds.includes(fold(t)));
        if (hasPref) base += 1;
      }

      return { ref, score: base };
    })
    .filter(({ ref, score }) => {
      if (score < MIN_SCORE) return false;
      if (whitelistSet.size > 0 && !whitelistSet.has(ref.key)) return false;
      if (!isRefAllowedAtStep(ref.key, step)) return false;
      return true;
    })
    .sort((a, b) => {
      let d = b.score - a.score;
      if (d === 0 && RANDOMIZE_TIED_SCORES) {
        d = (Math.random() - 0.5) * 0.0001; // tiny, stable-enough jitter
      }
      return (
        d ||
        (b.ref.year || 0) - (a.ref.year || 0) ||
        (a.ref.authors || "").localeCompare(b.ref.authors || "")
      );
    });

  const out = [];
  const seenAuthors = new Set();
  const seenInline = new Set();

  for (const { ref } of ranked) {
    const cite = toAPA7Inline(ref.citation);
    if (!cite) continue;

    // Block exact duplicate inlines, always.
    if (seenInline.has(cite)) continue;

    const akey = authorKeyFromInline(cite);
    if (!akey) continue;

    // Respect global excludes
    if (excludedKeys.has(akey)) continue;

    // Optional per-author dedupe
    if (CITATION_DEDUPE_AUTHORS && seenAuthors.has(akey)) continue;

    seenInline.add(cite);
    if (CITATION_DEDUPE_AUTHORS) seenAuthors.add(akey);

    out.push(cite);
    if (out.length >= MAX) break;
  }

  return out;
}

export function toAPA7Inline(str) {
  if (!str || typeof str !== "string") return "";

  // Normalise spaces
  const s = str.replace(/\s+/g, " ").trim();

  // Common patterns:
  //  A) "Author. (1993). Title …"
  //  B) "Author (1993) Title …"
  //  C) "Author, 1993"
  //  D) "Author & Author (1993) …"
  //  E) "Author, Author, & Author. (1993) …"
  //
  // Capture the author segment and the 4-digit year.
  const m =
    s.match(/^([\wÀ-ÿ'.\-&, ]+?)\s*\(\s*(\d{4})\s*\)/) || // A, B, D, E
    s.match(/^([\wÀ-ÿ'.\-&, ]+?)\s*,\s*(\d{4})\b/) || // C
    s.match(/^([\wÀ-ÿ'.\-&, ]+?)\s*\.\s*\(\s*(\d{4})\s*\)/); // A with period

  if (!m) return s; // fallback: return original string

  let authorPart = (m[1] || "").trim().replace(/\s*\.$/, ""); // drop trailing period
  const year = (m[2] || "").trim();

  // Reduce long author lists to "First Author & Others" style if needed.
  // Keep up to two author groups for readability.
  // Split on ",", " & ", and " and ".
  const splitAuthors = authorPart
    .split(/\s*(?:,|&| and )\s*/i)
    .map((a) => a.trim())
    .filter(Boolean);

  // normalise “et al.” consistently
  const cleaned = splitAuthors.map((a) =>
    a.replace(/\bet\s*al\.?/i, "et al.").trim(),
  );

  if (cleaned.length >= 3) {
    authorPart = `${cleaned[0]} & ${cleaned[1]}`;
  } else if (cleaned.length === 2) {
    authorPart = `${cleaned[0]} & ${cleaned[1]}`;
  } else {
    authorPart = cleaned[0] || authorPart;
  }

  // Final tidy: collapse spaces around punctuation
  authorPart = authorPart
    .replace(/\s+([,.;:])/, "$1")
    .replace(/\s{2,}/g, " ")
    .trim();

  return `${authorPart}, ${year}`;
}

function finalClean(text) {
  return (text || "")
    .replace(/^[\*\-\s]+/, "")
    .replace(/[\*\-]+$/, "")
    .replace(/\s*—\s*$/, "")
    .replace(/^#{1,6}\s*/, "")
    .replace(/\*\*/g, "")
    .replace(/^Step\s*\d+\s*[:-]?\s*/i, "")
    .replace(/\n+/g, "\n")
    .trim();
}

export function injectCitationsIntoText(
  text = "",
  references = [],
  { step = "", minSentenceLen = 60, max = 0 } = {},
) {
  if (!text || !Array.isArray(references) || references.length === 0)
    return text;

  // Step 1: still allow Aristotle if present (handled in getCitationsForStep)
  const sentences = text.split(/(?<=[.?!])\s+/);
  const citations = [
    ...new Set(references.slice(0, max).map((r) => `(${toAPA7Inline(r)})`)),
  ];
  let idx = 0;

  const out = sentences.map((s, i) => {
    const trimmed = s.trim();
    if (
      i === 0 ||
      trimmed.length < minSentenceLen ||
      looksLikeHeadingOrList(trimmed) ||
      hasExistingCitation(trimmed) ||
      idx >= citations.length
    ) {
      return s;
    }
    return s.replace(/([.?!])$/, ` ${citations[idx++]}$1`);
  });

  return out.join("\n\n");
}

// Map raw resolution → human label with one-word hint
export function prettyResolution(raw = "") {
  if (!raw) return "";
  const key = String(raw)
    .toLowerCase()
    .replace(/\s*\(.*?\)\s*$/, "");
  const MAP = {
    certain: "Certain (axiomatic)",
    definition: "Definition (semantic)",
    reconciliatory: "Reconciliatory (integrative)",
    irreconcilable: "Irreconcilable (paradox)",
    sublatable: "Sublatable (transformative)",
    empirical: "Empirical (evidence)",
    contextual: "Contextual (relative)",
    unknown: "Unknown (unspecified)",
    unclassified: "Unclassified (unspecified)",
  };
  if (key.includes("depends on definitions")) return MAP.definition;
  return MAP[key] ?? String(raw);
}

export function getRandomCitations(n = 2) {
  const pool = [...referencePool];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, n).map((ref) => ref.citation);
}

export function cleanSummary(text = "", title = "") {
  return (text || "")
    .toString()
    .replace(/^#+\s*/, "")
    .replace(/\*\*/g, "")
    .replace(new RegExp(`^${title}\\s*`, "i"), "")
    .replace(/^Step \d+[^:]*:\s*/i, "")
    .trim();
}

export function renderDomainBubble(epistemic = {}) {
  function safeDomain(dom) {
    if (!dom || (Array.isArray(dom) && dom.length === 0)) return ["symbolic"];
    const arr = Array.isArray(dom) ? dom : [dom];
    return arr.map((d) => {
      const low = (d + "").toLowerCase();
      return ["unclassified", "none", "undefined", ""].includes(low)
        ? "symbolic"
        : d;
    });
  }
  function safeField(f) {
    const low = (f + "").toLowerCase();
    return !f || ["unclassified", "none", "undefined", ""].includes(low)
      ? "symbolic"
      : f;
  }

  const {
    domain,
    primary,
    subtype,
    structure,
    resolution,
    resolution_display, // <-- use the hinted label if backend sent it
    notes,
  } = epistemic;

  const doms = safeDomain(domain);
  const prim = safeField(primary || doms[0]);
  const ordered = [prim, ...doms.filter((d) => d !== prim)];

  // Prefer backend-provided hinted label; otherwise prettify the raw resolution
  const resolutionText =
    typeof resolution_display === "string" && resolution_display.trim()
      ? resolution_display
      : prettyResolution(resolution);

  const colorMap = {
    epistemic: "bg-blue-700 text-white",
    ontological: "bg-purple-700 text-white",
    symbolic: "bg-green-700 text-white",
    phenomenological: "bg-yellow-600 text-black",
    "social/political": "bg-cyan-700 text-white",
    "ethics/morality": "bg-red-600 text-white",
    psychology: "bg-pink-500 text-white",
    biology: "bg-lime-600 text-white",
    aesthetic: "bg-indigo-700 text-white",
    law: "bg-gray-700 text-white",
    history: "bg-orange-600 text-white",
    economics: "bg-teal-700 text-white",
    medicine: "bg-emerald-700 text-white",
    unclassified: "bg-gray-600 text-white",
    mixed: "bg-pink-700 text-white",
  };

  return (
    <div className="bg-gray-800 text-white p-4 rounded-lg border border-gray-700 space-y-3 text-sm leading-relaxed">
      <div className="flex flex-wrap gap-2 mb-2">
        {ordered.map((d, i) => (
          <span
            key={i}
            className={`inline-block px-3 py-1 rounded-full text-xs uppercase tracking-wide font-bold ${
              colorMap[d.toLowerCase()] || colorMap.symbolic
            }`}
          >
            {d.toUpperCase()}
          </span>
        ))}
      </div>

      <div>
        <strong>Subtype:</strong> {capitalizeFirst(safeField(subtype))}
      </div>

      <div className="text-gray-400">
        <strong className="text-gray-300">Structure:</strong>{" "}
        <span className="text-white">
          {capitalizeFirst(safeField(structure))}
        </span>
      </div>

      <div className="text-gray-400">
        <strong className="text-gray-300">Resolution:</strong>{" "}
        <span className="text-white">
          {resolutionText || "Unknown (unspecified)"}{" "}
          {/* <-- now shows bracketed hint */}
        </span>
      </div>

      <div className="text-gray-400">
        <strong className="text-gray-300">Note:</strong>{" "}
        <span className="text-white">
          {capitalizeFirst(notes || "No notes.")}
        </span>
      </div>
    </div>
  );
}

export function renderTAS(
  thesis = "N/A",
  antithesis = "N/A",
  synthesis = "N/A",
  backendRefs = [], // pass in from API payload if available
) {
  const cleanSyn = finalClean(synthesis);
  const stepAwareCites = getCitationsForStep({
    text: cleanSyn,
    backendRefs,
    step: "Step 2",
    max: 1,
  });

  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-semibold text-blue-500 text-base">Thesis</h4>
        <p>{finalClean(thesis)}</p>
      </div>
      <div>
        <h4 className="font-semibold text-blue-500 text-base">Antithesis</h4>
        <p>{finalClean(antithesis)}</p>
      </div>
      <div>
        <h4 className="font-semibold text-blue-500 text-base">Synthesis</h4>
        <p>
          {injectCitationsIntoText(cleanSyn, stepAwareCites, {
            step: "Step 2",
          })}
        </p>
      </div>
    </div>
  );
}

export function renderStep1(contradictionText = "N/A", backendRefs = []) {
  const cleanContradiction = finalClean(contradictionText);

  // Pull citations with a Step 1 tag bias toward logic/computability
  const cites = getCitationsForStep({
    text: cleanContradiction,
    backendRefs,
    step: "Step 1",
    max: 1,
    preferTags: [
      "logic",
      "computability",
      "decidability",
      "theory-of-computation",
    ],
  });

  return (
    <div className="space-y-4">
      <h4 className="font-semibold text-blue-500 text-base">
        Step 1 – Contradiction Identification
      </h4>
      <p>
        {injectCitationsIntoText(cleanContradiction, cites, { step: "Step 1" })}
      </p>
    </div>
  );
}
const formatBayesValue = (value) => {
  if (typeof value !== "number" || isNaN(value)) {
    return "N/A";
  }

  if (value < 0.01) {
    return "<0.01";
  }

  return value.toFixed(2);
};

export function renderBayes(bayes = {}, title = "Bayesian Summary") {
  const prior = bayes.prior;
  const likelihood = bayes.likelihood;
  const posterior =
    bayes.step2_posterior ??
    bayes.posterior ??
    bayes.contradiction_probability ??
    0;
  return (
    <div className="mt-4 text-sm text-white space-y-1">
      <div className="font-semibold text-amber-500">{title}</div>
      {typeof prior === "number" && (
        <div>
          <span className="text-gray-400">Prior:</span>{" "}
          {formatBayesValue(prior)}
        </div>
      )}
      {typeof likelihood === "number" && (
        <div>
          <span className="text-gray-400">Likelihood:</span>{" "}
          {likelihood.toFixed(2)}
        </div>
      )}
      {typeof posterior === "number" ? (
        <div>
          <span className="text-gray-400">Posterior:</span>{" "}
          {formatBayesValue(posterior)}
        </div>
      ) : (
        <div>
          <span className="text-gray-400">Posterior:</span> N/A
        </div>
      )}
    </div>
  );
}

/**
 * Given an alternative reformulated question,
 * produce the "Try Asking ➙" suggestion.
 */
export function generateRelatedQuestion(baseQuestion) {
  if (!baseQuestion || baseQuestion === "N/A") return "";
  return baseQuestion;
}
